import { BadgeStatus, badgeStatusOf } from './CampaignPostRow.component'
import { CampaignPostDTO, CampaignStatus, PostStatus, SocialCampaignDTO } from 'models/socialCampaign'
import { FC, useCallback } from 'react'
import { PostDisplayStatus, formatCampaignDay, postDisplayStatus } from 'utils/socialCampaign/campaignStatus'
import { coverOf, waitingKindOf } from 'utils/socialCampaign/schedule'

import { CONTENT_KIND_WAYS } from './contentKinds'
import { CalendarPlacement } from 'utils/socialCampaign/calendar'
import { MediaThumb } from 'components/common/MediaThumb'
import { ProductIcon } from 'components/common/ProductIcon'
import { ScheduleOutlined } from '@mui/icons-material'
import styles from './CampaignCalendar.module.sass'
import { useTranslation } from 'react-i18next'

/**
 * A post's cover in the calendar (prototype v3 `slot` / `acard`): its first visual filling the box, a video's first
 * frame (no thumbnail is signed for it), the kind's glyph on a dashed tile for a post reserved for an order.
 */
export const CalendarPostCover: FC<{ post: CampaignPostDTO }> = ({ post }) => {
  const cover = coverOf(post)
  const kind = cover.kind === 'waiting' ? waitingKindOf(post) : null

  return (
    <>
      {cover.kind === 'waiting' &&
        <span className={styles.wait}>
          {kind ? <ProductIcon kind={CONTENT_KIND_WAYS[kind].icon} size="md" background="white" /> : <ScheduleOutlined sx={{ fontSize: '2rem' }} />}
        </span>
      }

      {cover.kind !== 'waiting' && !!cover.url &&
        <MediaThumb
          className={styles.cover}
          kind={cover.kind === 'video' ? 'video' : 'photo'}
          src={cover.kind === 'video' ? `${cover.url}#t=0.1` : cover.url}
          alt=""
        />
      }
    </>
  )
}

export interface CalendarPostSummary {
  /** The status bar's colour family, the agenda's badge */
  status: BadgeStatus
  /** "The garden down to the lake, Fri 16 Oct 18:00, Scheduled" */
  label: string
}

/**
 * What a post of the calendar says: the status its bar shows, and its accessible name, which names the status as the
 * list does (a draft campaign's drafts are "To review" or "Approved").
 */
export function useCalendarPostSummary(campaign: SocialCampaignDTO, now: Date) {
  const { t, i18n } = useTranslation(['social_campaign'])

  return useCallback(({ post, day, time }: CalendarPlacement): CalendarPostSummary => {
    const status = postDisplayStatus(post, campaign, now)
    const reviewMark = campaign.status === CampaignStatus.DRAFT && post.status === PostStatus.DRAFT
    const statusLabel = reviewMark && status !== PostDisplayStatus.APPROVED
      ? t('schedule.to_review')
      : t(`schedule.status.${reviewMark ? status : badgeStatusOf(status)}`)
    return {
      status: badgeStatusOf(status),
      label: t('schedule.calendar.post_label', { subject: post.subject, date: formatCampaignDay(day, i18n.language), time, status: statusLabel }),
    }
  }, [campaign, now, t, i18n.language])
}
